import { CalendarCheck, ShieldCheck, Clock, ExternalLink, Phone, Mail } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Kinetic";
import LimoWidget from "@/components/LimoWidget";
import { SITE, EMAILS } from "@/data/site";

const STEPS = [
  { no: "01", title: "Book online", desc: "Enter your trip — pickup, destination, vehicle class." },
  { no: "02", title: "Instant confirmation", desc: "Dispatch confirms your chauffeur and sends the details." },
  { no: "03", title: "Early arrival", desc: "Your chauffeur is in position ahead of schedule, tracking your flight." },
];

export default function Reservation() {
  return (
    <main data-testid="page-reservation" className="pt-20">
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12 lg:py-24">
        <Reveal className="text-center">
          <Eyebrow>Online Reservations · Powered by LimoAnywhere</Eyebrow>
          <h1 className="mx-auto mt-6 max-w-3xl font-display text-5xl font-semibold leading-[1.05] text-ivory sm:text-6xl">
            Reserve your <em className="gold-text italic">chauffeur.</em>
          </h1>
        </Reveal>

        <div className="mx-auto mt-16 grid max-w-4xl gap-6 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.no} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-gold/15 bg-obsidian-800 p-6 text-center">
                <p className="font-mono text-xs tracking-[0.3em] text-gold">{s.no}</p>
                <h3 className="mt-3 font-display text-xl font-semibold text-ivory">
                  {s.title}
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ivory/60">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8 lg:px-12">
        <Reveal>
          <LimoWidget type="website" minHeight={640} testId="reservation-widget" />
        </Reveal>

        <Reveal className="mt-6 text-center">
          <a
            href={SITE.booking}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reservation-open-new-tab-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ivory/50 transition-colors hover:text-gold"
          >
            Trouble loading? Book in a new window
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Reveal>
      </section>

      <section className="border-t border-gold/10 bg-obsidian-800/40">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-2 lg:px-12">
          <Reveal>
            <div className="flex items-start gap-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                <CalendarCheck className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-2xl font-semibold text-ivory">
                  Special requests?
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-ivory/65">
                  Multiple stops, a specific chauffeur, flowers in the cabin —
                  add them to the notes field when booking, and dispatch will
                  make it happen.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-start gap-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/30 text-gold">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-2xl font-semibold text-ivory">
                  Prefer a human?
                </h3>
                <p className="mt-2 text-sm text-ivory/65">
                  Dispatch answers around the clock.
                </p>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  {SITE.phone && (
                    <a
                      href={SITE.phoneHref}
                      data-testid="reservation-phone-link"
                      className="inline-flex items-center gap-2 text-gold transition-colors hover:text-gold-light"
                    >
                      <Phone className="h-4 w-4" /> {SITE.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${EMAILS.reservations}`}
                    data-testid="reservation-email-link"
                    className="inline-flex items-center gap-2 text-gold transition-colors hover:text-gold-light"
                  >
                    <Mail className="h-4 w-4" /> {EMAILS.reservations}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:px-12">
        <Reveal className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 pt-10">
          {[
            { icon: Clock, label: "Around-the-clock dispatch" },
            { icon: ShieldCheck, label: "Licensed & insured chauffeurs" },
            { icon: CalendarCheck, label: "Free cancellation window" },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory/45"
            >
              <Icon className="h-4 w-4 text-gold/70" />
              {label}
            </span>
          ))}
        </Reveal>
      </section>
    </main>
  );
}
